import React, { useEffect, useState } from "react";
import * as Mui from "@mui/material";
import { Box, Typography, IconButton, Paper } from "@mui/material";
import { DoubleArrow, DateRange } from "@mui/icons-material";
import { useForm } from "react-hook-form";
import DateRangePicker from "react-bootstrap-daterangepicker";
import moment from "moment";
import dayjs from "dayjs";
import { UserDetailsRow } from "components/CustomComponents/UserDetailsRow";
import { CustomTextBox } from "components/CustomComponents";
import { CustomButton } from "components/CustomComponents/CustomButton";
import { CustomTimePicker } from "components/CustomComponents/CustomTimePicker";
import { customAPI } from "services/utils/CustomAPI";
import { showToast } from "services/utils/Status";
import { useSelector } from "react-redux";
import { ConfirmationDialog } from "components/CustomComponents/ConfirmationDialog";

import {
  doubleArrowStyle,
  themes,
  parentBoxStyle,
  childBoxStyleWithColor,
  infoStyle,
  toAlignEnd,
  childBoxStyle,
} from "services/constants";

import {
  optionsStyle,
  cardStyle,
} from "pages/HR/Shift/Configuration/ShiftCategory/Component/ShiftCategoryOptions";
import { applicableTiming } from "../../Headers";

export const EditOTPreApproval = ({
  onClose,
  reCallApi,
  editOTPreAppDetails,
  setGetId,
  setHandleLoader,
}) => {
  const { domain, userDetails } = useSelector((state) => state.tokenReducer);
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const {
    setValue,
    handleSubmit,
    register,
    control,
    watch,
    formState: { errors },
  } = useForm({
    mode: "onBlur",
    defaultValues: {
      user_name: "",
      applicable_timing: "Only Before",
      start_date: moment(),
      end_date: moment(),
      reason_for_ot: "",
      durationHrs: dayjs("00:00", "HH:mm"),
      button_loader: false,
    },
  });

  useEffect(() => {
    if (!editOTPreAppDetails) return;

    let start = moment();
    let end = moment();
    if (
      editOTPreAppDetails?.ot_start_date &&
      editOTPreAppDetails?.ot_end_date
    ) {
      start = moment(editOTPreAppDetails.ot_start_date, "YYYY-MM-DD");
      end = moment(editOTPreAppDetails.ot_end_date, "YYYY-MM-DD");
    } else if (editOTPreAppDetails?.ot_date) {
      const parts = String(editOTPreAppDetails.ot_date).split(" - ");
      if (parts.length === 2) {
        const pStart = moment(parts[0], ["DD/MM/YYYY", "YYYY-MM-DD"]);
        const pEnd = moment(parts[1], ["DD/MM/YYYY", "YYYY-MM-DD"]);
        if (pStart.isValid()) start = pStart;
        if (pEnd.isValid()) end = pEnd;
      } else {
        const p = moment(editOTPreAppDetails.ot_date, [
          "DD/MM/YYYY",
          "YYYY-MM-DD",
        ]);
        if (p.isValid()) {
          start = p;
          end = p;
        }
      }
    }

    let durationValue = dayjs("00:00", "HH:mm");
    if (editOTPreAppDetails?.ot_duration) {
      const dur = editOTPreAppDetails.ot_duration;
      if (/^\d{1,2}:\d{2}$/.test(dur)) {
        durationValue = dayjs(dur, "HH:mm");
      } else if (!isNaN(Number(dur))) {
        const hours = Math.floor(Number(dur));
        const minutes = Math.round((Number(dur) - hours) * 60);
        durationValue = dayjs().hour(hours).minute(minutes);
      } else {
        const tryParse = dayjs(dur, ["H:mm", "HH:mm"]);
        if (tryParse.isValid()) durationValue = tryParse;
      }
    }

    setValue("start_date", start);
    setValue("end_date", end);
    setValue("durationHrs", durationValue);
    setValue(
      "applicable_timing",
      editOTPreAppDetails?.ot_consideration ?? "Only Before"
    );
    setValue("reason_for_ot", editOTPreAppDetails?.reason ?? "");
    setValue("user_name", userDetails?.username ?? "");
  }, [editOTPreAppDetails, setValue, userDetails]);

  const label =
    watch("start_date")?.format("DD/MM/YYYY") +
    " - " +
    watch("end_date")?.format("DD/MM/YYYY");

  const handleCallback = async (startDate, endDate) => {
    setValue("start_date", moment(startDate));
    setValue("end_date", moment(endDate));
  };

  const hanldeApplicableTiming = (event) => {
    setValue("applicable_timing", event.target.value);
  };

  const handleReasonForOt = (event) => {
    if (event.target.value.length <= 1024) {
      setValue("reason_for_ot", event.target.value);
    }
  };

  const onSubmit = async (data) => {
    setValue("button_loader", true);

    if (
      !data.start_date ||
      !data.end_date ||
      !moment(data.start_date).isValid() ||
      !moment(data.end_date).isValid()
    ) {
      setValue("button_loader", false);
      showToast("error", "Please select a valid OT start and end date.");
      return;
    }
    if (moment(data.end_date).isBefore(moment(data.start_date))) {
      setValue("button_loader", false);
      showToast("error", "End date cannot be before start date.");
      return;
    }

    if (!data.durationHrs || !dayjs(data.durationHrs).isValid()) {
      setValue("button_loader", false);
      showToast("error", "Duration hours is required");
      return;
    }

    const payload = {
      manual_ot_id: editOTPreAppDetails?.id,
      user_id: userDetails?.id,
      ot_start_date: moment(data.start_date).format("YYYY-MM-DD"),
      ot_end_date: moment(data.end_date).format("YYYY-MM-DD"),
      ot_duration: dayjs(data.durationHrs).format("HH:mm"),
      ot_consideration: data.applicable_timing,
      reason: data.reason_for_ot,
      ...(editOTPreAppDetails?.status === "Approved" && {
        status: "Pending",
      }),
    };

    const id = editOTPreAppDetails?.id;

    if (!id) {
      setValue("button_loader", false);
      showToast("error", "Missing OT request id. Cannot update.");
      return;
    }

    try {
      const url = `${domain}My_Zone/Apply/Over_time/ot-pre-approval/`;
      const response = await customAPI(url, "PUT", payload);

      if (response.ok) {
        showToast(
          "success",
          response?.responseDetails?.message ?? "OT updated successfully"
        );
        setValue("button_loader", false);
        onClose();
        setHandleLoader(true);
        reCallApi(true);
        if (setGetId) setGetId(id);
      } else {
        setValue("button_loader", false);
        showToast("error", response?.message ?? "Failed to update OT");
      }
    } catch (err) {
      setValue("button_loader", false);
      showToast("error", err?.message ?? "Something went wrong");
    }
  };

  const handleCancelClose = () => {
    setShowConfirmationDialog(false);
  };

  return (
    <Box sx={parentBoxStyle}>
      <ConfirmationDialog
        open={showConfirmationDialog}
        onClose={handleCancelClose}
        onConfirm={handleSubmit(onSubmit)}
        title={"OverTime"}
        message={"Re update your status"}
        confirmText={"Update"}
        cancelText="Cancel"
      />
      <Paper
        elevation={0}
        sx={{
          borderBottom: `1px solid ${themes?.miliedLightGray}`,
          borderRadius: 0,
        }}
      >
        <Box sx={childBoxStyleWithColor}>
          <Box sx={{ ml: 1 }}>
            <IconButton onClick={onClose} sx={doubleArrowStyle}>
              <DoubleArrow />
            </IconButton>
          </Box>

          <Box sx={{ ml: 2.2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Edit OT Pre-Approval Request
            </Typography>
            <Typography {...infoStyle}>
              Edit OT request by updating the requirement.
            </Typography>
          </Box>
        </Box>
      </Paper>

      <Box
        sx={{ display: "flex", flexDirection: "column", gap: 2.5, p: 2, pb: 0 }}
      >
        <Box>
          <UserDetailsRow
            userDetails={{
              image_url: editOTPreAppDetails?.image_url,
              employee_id: editOTPreAppDetails?.employee_id
                ? editOTPreAppDetails?.employee_id
                : "-",
              user_name: editOTPreAppDetails?.employee_name,
              department_name: editOTPreAppDetails?.department
                ? editOTPreAppDetails?.department
                : "-",
              designation: editOTPreAppDetails?.user_type
                ? editOTPreAppDetails?.user_type
                : "-",
            }}
          />
        </Box>
      </Box>

      <Box sx={{ flexGrow: 1, overflow: "auto", p: 2 }}>
        <Box sx={childBoxStyle}>
          <Box>
            <Typography sx={{ fontWeight: 600 }}>
              Overtime Date / Period
            </Typography>
            <Typography {...infoStyle}>
              Update the start and end dates for the overtime period.
            </Typography>
            <DateRangePicker
              initialSettings={{
                startDate: watch("start_date")?.toDate(),
                endDate: watch("end_date")?.toDate(),
                applyClass: "custom-apply-btn",
                cancelClass: "custom-cancel-btn",
                locale: { format: "DD/MM/YYYY" },
              }}
              onCallback={handleCallback}
            >
              <Mui.TextField
                id="reportrange_edit"
                variant="outlined"
                label=""
                placeholder="Select a date range"
                value={label}
                size="small"
                InputProps={{
                  readOnly: true,
                  notched: false,
                  endAdornment: (
                    <Mui.InputAdornment position="end">
                      <Mui.IconButton>
                        <DateRange />
                      </Mui.IconButton>
                    </Mui.InputAdornment>
                  ),
                }}
                InputLabelProps={{ shrink: true, required: true }}
                fullWidth
                sx={{
                  "& .MuiOutlinedInput-notchedOutline": {
                    legend: { display: "none" },
                  },
                }}
              />
            </DateRangePicker>
          </Box>

          <Box>
            <Typography sx={{ fontWeight: 600 }}>Duration in Hrs</Typography>
            <Typography {...infoStyle}>
              Update the maximum allowed time in Hrs: as per OT Policy
            </Typography>
            <CustomTimePicker
              name="durationHrs"
              control={control}
              errors={errors}
              validateFn={(value) => {
                if (!value || !dayjs(value).isValid()) {
                  return "Duration hours is required";
                }
                return true;
              }}
            />
          </Box>

          <Box>
            <Typography sx={{ fontWeight: 600 }}>Applicable Timing</Typography>
            <Typography {...infoStyle}>
              Update when the overtime will be considered.
            </Typography>
            <Box>
              <Mui.RadioGroup
                row
                value={watch("applicable_timing")}
                onChange={hanldeApplicableTiming}
                sx={optionsStyle}
              >
                {applicableTiming.map((opt) => {
                  const isActive = watch("applicable_timing") === opt.id;
                  return (
                    <Box key={opt.id} sx={cardStyle(isActive)}>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Mui.Radio value={opt.id} />
                        <Typography
                          fontWeight="medium"
                          sx={{
                            color: isActive
                              ? themes.stepActiveColor
                              : "inherit",
                          }}
                        >
                          {opt.label}
                        </Typography>
                      </Box>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ ml: 4, maxWidth: 250, wordBreak: "break-word" }}
                      >
                        {opt.description}
                      </Typography>
                    </Box>
                  );
                })}
              </Mui.RadioGroup>
            </Box>
          </Box>

          <Box>
            <Typography sx={{ fontWeight: 600 }}>Reason</Typography>
            <Typography {...infoStyle}>
              Update the purpose or justification for the overtime request.
            </Typography>
            <CustomTextBox
              type="multiline"
              rules={{
                ...register("reason_for_ot", {
                  required: "Reason for overtime is required",
                  validate: {
                    minLengthNoSpaces: (value) => {
                      const trimmed = value?.trim();
                      if (!trimmed || trimmed.length < 3) {
                        return "Minimum 3 characters required";
                      }
                      if (trimmed.length > 255) {
                        return "Maximum length is 255 characters";
                      }
                      return true;
                    },
                    atleastOneChar: (value) => {
                      if (!/[A-Za-z]/.test(value)) {
                        return "At least one alphabet character required";
                      }
                      return true;
                    },
                  },
                }),
              }}
              selectedValue={watch("reason_for_ot")}
              updateDataFunction={handleReasonForOt}
              placeHolder="Provide a reason for overtime"
              errorMessage={
                errors?.reason_for_ot?.message
                  ? errors?.reason_for_ot?.message
                  : ""
              }
              textBoxError={errors?.reason_for_ot ? true : false}
            />
          </Box>

          <Box sx={toAlignEnd}>
            <CustomButton
              actionFuntion={() => {
                if (editOTPreAppDetails?.status === "Approved") {
                  setShowConfirmationDialog(true);
                } else {
                  handleSubmit(onSubmit)();
                  setShowConfirmationDialog(false);
                }
              }}
              actionName="Update"
              typeName="submit"
              disableAction={watch("button_loader")}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default EditOTPreApproval;
